import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, inject, input, OnInit, output } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { InputText, Button, TextValueAccessor, BooleanValueAccessor } from '@shui/angular';
import {
    ShFileInputComponent,
    shsThemeDark,
    ShDatepickerAccessorComponent,
    ParallelReferralDialog,
    ParallelReferralDialogData,
    ParallelReferralDialogResult,
    ExternalReferralDialog,
    ExternalReferralDialogData,
    ExternalReferralDialogResult
} from '@shs-digi-care-monorepo/frontend-lib';
import { AgGridAngular } from 'ag-grid-angular';
import type { ColDef } from 'ag-grid-community';
import { IS3File } from '@shs-digi-care-monorepo/interfaces';
import { DealType } from '../../models/deal-type.model';
import { productLineLabelKey } from '../../../shared/models/temp.model';
import { OneDxCustomerInformation, OneDxMaterialItem } from '../../../shared/models/onedx.model';
import { createOverviewFormGroup, OverviewFormGroup } from '../../models/overview.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { filter, tap } from 'rxjs';

export interface OverviewContactData {
    customerEmail: string | null;
    tasEmail: string | null;
    salesRepEmail: string | null;
    pmInstallationEmail: string | null;
    additionalContacts: { title: string | null; email: string | null }[];
}

export interface OverviewOrderDetailsData {
    isDistributor: boolean | null;
    distributorName: string | null;
    isCprContract: boolean | null;
    monthsOfInventory: number | null;
    additionalEmails: string[] | null;
}

@Component({
    selector: 'app-overview',
    templateUrl: './overview.component.html',
    imports: [
        TranslateModule,
        ReactiveFormsModule,
        InputText,
        Button,
        TextValueAccessor,
        ShFileInputComponent,
        BooleanValueAccessor,
        AgGridAngular,
        ShDatepickerAccessorComponent
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class OverviewForm implements OnInit {
    public dealType = input<DealType | null>(null);
    public customerInformation = input<OneDxCustomerInformation | null>(null);
    public productLine = input<string | null>(null);
    public soldToNumber = input<string | null>(null);
    public shipToNumber = input<string | null>(null);
    public materials = input<OneDxMaterialItem[]>([]);
    public contactData = input<OverviewContactData | null>(null);
    public orderDetailsData = input<OverviewOrderDetailsData | null>(null);
    public requestId = input<string | null>(null);
    public loading = input<boolean>(false);
    public serverFiles = input<IS3File[]>([]);

    public saveChanged = output<void>();
    public submitChanged = output<void>();
    public deleteChanged = output<void>();
    public editStepChanged = output<number>();
    public attachmentDownload = output<string>();
    public attachmentDelete = output<string>();
    public parallelReferralSubmitted = output<ParallelReferralDialogResult>();
    public externalReferralSubmitted = output<ExternalReferralDialogResult>();

    private translate = inject(TranslateService);
    private dialog = inject(MatDialog);
    private destroyRef = inject(DestroyRef);

    public formGroup: FormGroup<OverviewFormGroup> = createOverviewFormGroup();

    protected DealType = DealType;

    protected productLineKey = computed(() => {
        const value = this.productLine();
        return value ? productLineLabelKey(value) : '';
    });

    protected instrumentOnlyColDefs: ColDef[] = [
        { field: 'productLine', headerName: this.translate.instant('iro.grid.product-line') },
        { field: 'description', headerName: this.translate.instant('iro.grid.description') },
        { field: 'smn', headerName: this.translate.instant('iro.grid.smn') },
        { field: 'kitSizeDescription', headerName: this.translate.instant('iro.grid.kit-size-description') },
        { field: 'kitSize', headerName: this.translate.instant('iro.grid.kit-size') },
        { field: 'annualTestVolume', headerName: this.translate.instant('iro.grid.annual-test-volume') },
        { field: 'inventoryAmt', headerName: this.translate.instant('iro.grid.inventory-amount') },
        { field: 'implementationQuantity', headerName: this.translate.instant('iro.grid.implementation-quantity') },
        { field: 'orderQuantity', headerName: this.translate.instant('iro.grid.order-quantity') },
        { field: 'unitPrice', headerName: this.translate.instant('iro.grid.unit-price') },
        { field: 'totalPrice', headerName: this.translate.instant('iro.grid.total-price') }
    ];

    protected standardDealColDefs: ColDef[] = [
        { field: 'productLine', headerName: this.translate.instant('iro.grid.product-line') },
        { field: 'description', headerName: this.translate.instant('iro.grid.description') },
        { field: 'smn', headerName: this.translate.instant('iro.grid.smn') },
        { field: 'kitSizeDescription', headerName: this.translate.instant('iro.grid.kit-size-description') },
        { field: 'kitSize', headerName: this.translate.instant('iro.grid.kit-size') },
        { field: 'annualTestVolume', headerName: this.translate.instant('iro.grid.annual-test-volume') }
    ];

    protected colDefs = computed<ColDef[]>(() => {
        return this.dealType() === DealType.STANDARD_DEAL ? this.standardDealColDefs : this.instrumentOnlyColDefs;
    });

    protected gridOptions = {
        theme: shsThemeDark
    };

    public ngOnInit(): void {
        this.initCheckboxMutualExclusion();
    }

    protected onSave(): void {
        this.saveChanged.emit();
    }

    protected onSubmit(): void {
        if (this.formGroup.controls.directReferOutInternal.value) {
            this.openParallelReferralDialog();
            return;
        }
        if (this.formGroup.controls.directReferOutExternal.value) {
            this.openExternalReferralDialog();
            return;
        }
        this.submitChanged.emit();
    }

    protected onDelete(): void {
        this.deleteChanged.emit();
    }

    protected onEditStep(stepIndex: number): void {
        this.editStepChanged.emit(stepIndex);
    }

    protected onAttachmentDownload(fileKey: string): void {
        this.attachmentDownload.emit(fileKey);
    }

    protected onAttachmentDelete(fileKey: string): void {
        this.attachmentDelete.emit(fileKey);
    }

    private initCheckboxMutualExclusion(): void {
        const { informationalOnly, directReferOutInternal, directReferOutExternal } = this.formGroup.controls;

        informationalOnly.valueChanges
            .pipe(
                tap((checked) => {
                    if (checked) {
                        directReferOutInternal.setValue(false, { emitEvent: false });
                        directReferOutExternal.setValue(false, { emitEvent: false });
                        directReferOutInternal.disable({ emitEvent: false });
                        directReferOutExternal.disable({ emitEvent: false });
                    } else {
                        directReferOutInternal.enable({ emitEvent: false });
                        directReferOutExternal.enable({ emitEvent: false });
                    }
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();

        directReferOutInternal.valueChanges
            .pipe(
                tap((checked) => {
                    if (checked) {
                        informationalOnly.setValue(false, { emitEvent: false });
                        directReferOutExternal.setValue(false, { emitEvent: false });
                        informationalOnly.disable({ emitEvent: false });
                        directReferOutExternal.disable({ emitEvent: false });
                    } else {
                        informationalOnly.enable({ emitEvent: false });
                        directReferOutExternal.enable({ emitEvent: false });
                    }
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();

        directReferOutExternal.valueChanges
            .pipe(
                tap((checked) => {
                    if (checked) {
                        informationalOnly.setValue(false, { emitEvent: false });
                        directReferOutInternal.setValue(false, { emitEvent: false });
                        informationalOnly.disable({ emitEvent: false });
                        directReferOutInternal.disable({ emitEvent: false });
                    } else {
                        informationalOnly.enable({ emitEvent: false });
                        directReferOutInternal.enable({ emitEvent: false });
                    }
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    private openParallelReferralDialog(): void {
        this.dialog
            .open<ParallelReferralDialog, ParallelReferralDialogData, ParallelReferralDialogResult>(
                ParallelReferralDialog,
                {
                    panelClass: 'sheet-panel-right',
                    hasBackdrop: true,
                    data: {
                        supportsParallelReferral: true,
                        canMarkCommentAsInternal: false,
                        isReferredOutAfterSubmission: true
                    }
                }
            )
            .afterClosed()
            .pipe(
                filter((result) => !!result),
                tap((result) => this.parallelReferralSubmitted.emit(result)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    private openExternalReferralDialog(): void {
        this.dialog
            .open<ExternalReferralDialog, ExternalReferralDialogData, ExternalReferralDialogResult>(
                ExternalReferralDialog,
                {
                    panelClass: 'sheet-panel-right',
                    hasBackdrop: true,
                    data: {
                        canMarkCommentAsInternal: false,
                        isReferredOutAfterSubmission: true
                    }
                }
            )
            .afterClosed()
            .pipe(
                filter((result) => !!result),
                tap((result) => this.externalReferralSubmitted.emit(result)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }
}
